import _ from 'lodash'
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import orderModel, { CreateOrderDto, Order, UpdateOrderDto } from '@/core/models/order.model'
import { SearchOrdersQuery, SearchRequestOptions } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'

export const orderAdapter = createEntityAdapter<Order, Order['_id']>({ selectId: (order) => order._id })

const orderSelector = orderAdapter.getSelectors((state: RootState) => state.order.orders)

export const selectOrders = orderSelector.selectAll
export const selectOrderById = orderSelector.selectById
export const selectOrderByReferenceId = (state: RootState, referenceId: string) => _.find(selectOrders(state), (order) => order.referenceId === referenceId)
export const selectNewlyCreatedOrderId = (state: RootState) => state.order.newlyCreatedItemId
export const selectOrderCreateRequestTracker = (state: RootState) => state.order.requestTracker.create
export const selectOrderUpdateRequestTracker = (state: RootState) => state.order.requestTracker.update
export const selectOrderSearchParams = (state: RootState) => state.order.search.params
export const selectOrderSearchResult = (state: RootState) => state.order.search.result

const initialState = _.merge(orderModel.getInitialState(), {})

const orderSlice = createSlice({
  name: orderModel.reducerName,
  initialState,
  reducers: {
    createOrder(state, action: PayloadAction<CreateOrderDto>) {
      state.requestTracker.create.loading = true
      state.newlyCreatedItemId = undefined
    },
    createOrderSuccess(state, action: PayloadAction<Order>) {
      const order = action.payload
      orderAdapter.setOne(state.orders, order)
      state.newlyCreatedItemId = order._id
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createOrderFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    getOrderByReferenceId(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getOrder(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getOrderSuccess(state, action: PayloadAction<Order>) {
      orderAdapter.setOne(state.orders, action.payload)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getOrderFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    listOrders: {
      reducer: (state, action: PayloadAction<{ query: SearchOrdersQuery; options?: SearchRequestOptions }>) => {
        state.search.params.query = action.payload.query
        state.search.params.options = action.payload.options
        state.requestTracker.list.loading = true
      },
      prepare: (query: SearchOrdersQuery, options?: SearchRequestOptions) => ({
        payload: {
          query,
          options,
        },
      }),
    },
    listOrdersSuccess(state, action: PayloadAction<{ items: Order[]; totalCount: number }>) {
      const { items, totalCount } = action.payload
      orderAdapter.setAll(state.orders, items)
      state.search.result.totalCount = totalCount
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    listOrdersFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    updateOrder: {
      reducer(state, action: PayloadAction<{ orderId: string; updateOrderDto: UpdateOrderDto }>) {
        state.requestTracker.update.loading = true
      },
      prepare: (orderId: string, updateOrderDto: UpdateOrderDto) => ({
        payload: { orderId, updateOrderDto },
      }),
    },
    updateOrderSuccess(state, action: PayloadAction<Order>) {
      orderAdapter.setOne(state.orders, action.payload)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateOrderFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
  },
})

export const {
  createOrder,
  createOrderSuccess,
  createOrderFailure,
  getOrderByReferenceId,
  getOrder,
  getOrderSuccess,
  getOrderFailure,
  listOrders,
  listOrdersSuccess,
  listOrdersFailure,
  updateOrder,
  updateOrderSuccess,
  updateOrderFailure,
} = orderSlice.actions

export default orderSlice.reducer
